/**
 * HandoffNotes.jsx — Department handoff notes panel for job detail
 * Threaded, timestamped notes for Originator / Production / Post-Production
 */

import { useState } from 'react';
import { Box, Paper, Typography, Button, IconButton, TextField, MenuItem, Select, FormControl, InputLabel, Stack } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import { useFocus } from '../../hooks/useFocus';

const DEPARTMENTS = [
  { value: 'originator', label: 'Originator', color: '#D2691E', emoji: '🟠' },
  { value: 'production', label: 'Production', color: '#13B5EA', emoji: '🔵' },
  { value: 'post_production', label: 'Post-Production', color: '#10B981', emoji: '🟢' },
];

const DEPT_MAP = Object.fromEntries(DEPARTMENTS.map((d) => [d.value, d]));

function genId() {
  return 'hn_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
}

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

export default function HandoffNotes({ notes = [], currentUser, onSave, saving }) {
  const [showForm, setShowForm] = useState(false);
  const [dept, setDept] = useState('originator');
  const [text, setText] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [expanded, setExpanded] = useState(false);
  const { isHidden } = useFocus();

  const sorted = [...notes].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const handleAdd = async () => {
    if (!text.trim()) return;
    const entry = {
      id: genId(),
      department: dept,
      author: currentUser,
      text: text.trim(),
      created_at: new Date().toISOString(),
    };
    const ok = await onSave([...notes, entry]);
    if (ok !== false) {
      setText('');
      setShowForm(false);
    }
  };

  const handleDelete = async (id) => {
    await onSave(notes.filter((n) => n.id !== id));
  };

  const handleEditStart = (note) => {
    setEditingId(note.id);
    setEditText(note.text);
  };

  const handleEditSave = async () => {
    if (!editText.trim()) return;
    const ok = await onSave(notes.map((n) => n.id === editingId ? { ...n, text: editText.trim(), edited_at: new Date().toISOString() } : n));
    if (ok !== false) {
      setEditingId(null);
      setEditText('');
    }
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditText('');
  };

  if (sorted.length === 0 && !showForm && isHidden('job.handoffNotes')) return null;

  return (
    <Paper data-focus="job.handoffNotes" sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: sorted.length > 0 || showForm ? 1.5 : 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', fontFamily: '"DM Mono", monospace', textTransform: 'uppercase', fontSize: '0.6rem' }}>
          Notes{sorted.length > 0 ? ` (${sorted.length})` : ''}
        </Typography>
        {!showForm && (
          <Button size="small" startIcon={<AddIcon sx={{ fontSize: 14 }} />} onClick={() => setShowForm(true)} sx={{ fontSize: '0.7rem', textTransform: 'none', fontFamily: '"DM Mono", monospace', minWidth: 0, px: 1 }}>
            Add
          </Button>
        )}
      </Box>

      {/* Existing notes */}
      <Box sx={{ maxHeight: expanded ? 'none' : 300, overflowY: expanded ? 'visible' : 'auto', transition: 'max-height 0.2s ease' }}>
      {sorted.map((note, i) => {
        const d = DEPT_MAP[note.department] || DEPARTMENTS[0];
        const isEditing = editingId === note.id;
        return (
          <Box key={note.id} sx={{ borderBottom: i < sorted.length - 1 ? '1px solid' : 'none', borderColor: 'divider', py: 1.25, '&:first-of-type': { pt: 0 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography sx={{ fontSize: 12, lineHeight: 1 }}>{d.emoji}</Typography>
                <Typography sx={{ fontSize: '0.65rem', fontFamily: '"DM Mono", monospace', fontWeight: 700, color: d.color, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {d.label}
                </Typography>
                <Typography sx={{ fontSize: '0.65rem', fontFamily: '"DM Mono", monospace', color: 'text.disabled' }}>
                  · {note.author} · {timeAgo(note.created_at)}{note.edited_at ? ' (edited)' : ''}
                </Typography>
              </Box>
              {!isEditing && (
                <Box sx={{ display: 'flex', gap: { xs: 1, md: 0 } }}>
                  <IconButton size="small" onClick={() => handleEditStart(note)} sx={{ opacity: { xs: 0.7, md: 0.4 }, '&:hover': { opacity: 1 }, p: { xs: 1, md: 0.5 } }}>
                    <EditIcon sx={{ fontSize: 13 }} />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(note.id)} sx={{ opacity: { xs: 0.7, md: 0.4 }, '&:hover': { opacity: 1, color: 'error.main' }, p: { xs: 1, md: 0.5 } }}>
                    <DeleteIcon sx={{ fontSize: 13 }} />
                  </IconButton>
                </Box>
              )}
            </Box>
            {isEditing ? (
              <Box sx={{ mt: 0.5 }}>
                <TextField
                  fullWidth size="small" multiline minRows={2} value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  sx={{ '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
                />
                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.75, justifyContent: 'flex-end' }}>
                  <Button size="small" onClick={handleEditCancel} sx={{ fontSize: '0.7rem', textTransform: 'none', minWidth: 0, px: 1 }}>Cancel</Button>
                  <Button size="small" variant="contained" onClick={handleEditSave} disabled={saving || !editText.trim()} sx={{ fontSize: '0.7rem', textTransform: 'none', minWidth: 0, px: 1.5 }}>Save</Button>
                </Box>
              </Box>
            ) : (
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{note.text}</Typography>
            )}
          </Box>
        );
      })}
      </Box>

      {/* Show all / Show less toggle */}
      {sorted.length > 4 && (
        <Box sx={{ textAlign: 'center', pt: 0.75 }}>
          <Button size="small" onClick={() => setExpanded(!expanded)} sx={{ fontSize: '0.65rem', textTransform: 'none', fontFamily: '"DM Mono", monospace', color: 'text.disabled', minWidth: 0, px: 1 }}>
            {expanded ? 'Show less' : `Show all ${sorted.length} notes`}
          </Button>
        </Box>
      )}

      {/* Empty state */}
      {sorted.length === 0 && !showForm && (
        <Typography variant="body2" color="text.disabled" sx={{ fontFamily: '"DM Mono", monospace', fontSize: '0.7rem', mt: 0.5 }}>
          No department notes yet
        </Typography>
      )}

      {/* Add form */}
      {showForm && (
        <Box sx={{ mt: sorted.length > 0 ? 1.5 : 0, pt: sorted.length > 0 ? 1.5 : 0, borderTop: sorted.length > 0 ? '1px solid' : 'none', borderColor: 'divider' }}>
          <FormControl size="small" sx={{ mb: 1, minWidth: 160 }}>
            <InputLabel sx={{ fontSize: '0.8rem' }}>Department</InputLabel>
            <Select value={dept} label="Department" onChange={(e) => setDept(e.target.value)} sx={{ fontSize: '0.8rem' }}>
              {DEPARTMENTS.map((d) => (
                <MenuItem key={d.value} value={d.value} sx={{ fontSize: '0.8rem' }}>
                  {d.emoji} {d.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth size="small" multiline minRows={2} placeholder="Add a note for this department..."
            value={text} onChange={(e) => setText(e.target.value)}
            sx={{ mb: 1, '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
          />
          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={() => { setShowForm(false); setText(''); }} sx={{ fontSize: '0.7rem', textTransform: 'none' }}>Cancel</Button>
            <Button size="small" variant="contained" onClick={handleAdd} disabled={saving || !text.trim()} sx={{ fontSize: '0.7rem', textTransform: 'none' }}>
              {saving ? 'Saving...' : 'Add Note'}
            </Button>
          </Box>
        </Box>
      )}
    </Paper>
  );
}
